import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { Answer, Enrolment } from "../../../stores/EnrolmentStore/Types";
import { CourseReadingResource } from "../../../stores/CoursesStore/Types";
import { TopicPreview } from "../../../stores/CoursePreviewStore/Types";
import { getRequest } from "../../../utils/httpRequest";
import { errorNotification } from "../../../utils/helpers";
import NewReviewModal from "../NewReviewModal";
import PersonalizationModal from "../Personalization/PersonalizationModal";
import { RepersonalizationModal } from "../Personalization/RepersonalizationModal";
import ChaptersPanel from "./components/ChaptersPanel";
import LivesPanel from "./components/LivesPanel";
import Masthead from "./components/Masthead";
import ResourcesPanel from "./components/ResourcesPanel";
import StartCard from "./components/StartCard";
import Tabs from "./components/Tabs";
import { getCourseOverviewState } from "./helpers";
import { ActiveTab } from "./types";
import Icons from "./components/Icons";

interface Props {
  match?: any;
  currentUser: any;
  mode: string;
  enrolment: Enrolment;
}

interface State {
  activeTab: ActiveTab;
  answers: Answer[];
  chapters: TopicPreview[];
  enrolment: Enrolment;
  isPersonalizationModalOpen: boolean;
  isRepersonalizationModalOpen: boolean;
  loading: boolean;
  loadingChapters: boolean;
  loadingResources: boolean;
  personalizationState: string;
  resources: CourseReadingResource[];
  showReviewModal: boolean;
}

class CourseOverview extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: this.resolveActiveTab(props.mode),
      answers: [],
      chapters: [],
      enrolment: null,
      isPersonalizationModalOpen: false,
      isRepersonalizationModalOpen: false,
      loading: true,
      loadingChapters: true,
      loadingResources: true,
      personalizationState: "",
      resources: [],
      showReviewModal: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    this.loadEnrolment();
  }

  resolveActiveTab = (mode?: string): ActiveTab => {
    return ["chapters", "resources", "lives"].includes(mode)
      ? (mode as ActiveTab)
      : "chapters";
  };

  loadEnrolment = () => {
    const enrolmentId =
      this.props.match?.params?.enrolmentId || this.props.enrolment?.id;

    return getRequest(`/api/enrolments/${enrolmentId}.json`)
      .then((response: AxiosResponse) => {
        if (
          response.data.state === "passed" &&
          response.data?.can_leave_review
        ) {
          this.handleShowReviewModal();
        } else if (response.data.embedded_host_url) {
          window.location = response.data.embedded_host_url;
        }

        this.setState(
          {
            enrolment: response.data,
            loading: false,
            personalizationState: response.data.personalization_state || "",
          },
          () => {
            this.loadChapters(response.data);
            this.loadAnswers(response.data);
            this.loadResources(response.data);
          },
        );
      })
      .catch((error) => {
        this.setState({ loading: false, loadingChapters: false });

        errorNotification(
          "Error loading enrolment data",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  loadChapters = (enrolment: Enrolment) => {
    return getRequest(`/api/courses/${enrolment?.course.slug}/topics.json?view=nav`)
      .then((response: AxiosResponse) => {
        this.setState({ chapters: response.data, loadingChapters: false });
      })
      .catch((error) => {
        this.setState({ loadingChapters: false });

        errorNotification(
          "Error loading chapters",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  loadAnswers = (enrolment: Enrolment) => {
    return getRequest(`/api/answers.json?enrolment_id=${enrolment?.id}&view=nav`)
      .then((response: AxiosResponse) => {
        this.setState({ answers: response.data });
      })
      .catch((error) => {
        errorNotification(
          "Error loading answers",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  loadResources = (enrolment: Enrolment) => {
    return getRequest(
      `/api/resources.json?enrolment_id=${enrolment?.id}&sort_by=popular`,
    )
      .then((response: AxiosResponse) => {
        this.setState({ resources: response.data, loadingResources: false });
      })
      .catch((error) => {
        this.setState({ loadingResources: false });

        errorNotification(
          "Error loading resources",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  handleShowReviewModal = () => {
    setTimeout(() => {
      this.setState({ showReviewModal: true });
    }, 5000);
  };

  handleHideNewReviewModal = () => {
    this.setState({ showReviewModal: false });
  };

  handleChangePersonalization = (newState: string) => {
    this.setState({ personalizationState: newState });
  };

  handlePersonalizationModalStateChange = (newState: string) => {
    this.setState({
      personalizationState: newState,
      isPersonalizationModalOpen: ![
        "complete",
        "generating_progress",
        "skipped",
      ].includes(newState),
    });
  };

  openPersonalizationModal = () => {
    this.setState({ isPersonalizationModalOpen: true });
  };

  closePersonalizationModal = () => {
    this.setState({ isPersonalizationModalOpen: false });
  };

  openRepersonalizationModal = () => {
    this.setState({ isRepersonalizationModalOpen: true });
  };

  closeRepersonalizationModal = () => {
    this.setState({ isRepersonalizationModalOpen: false });
  };

  setActiveTab = (activeTab: ActiveTab) => {
    this.setState({ activeTab });
  };

  resolvePersonalizationState = () => {
    const { enrolment, personalizationState } = this.state;

    return personalizationState || enrolment?.personalization_state;
  };

  renderLoading = () => (
    <div className="container">
      <div className="ui active inverted dimmer">
        <div className="ui text loader">Loading</div>
      </div>
    </div>
  );

  renderPersonalizationModals = () => {
    const {
      enrolment,
      isPersonalizationModalOpen,
      isRepersonalizationModalOpen,
      personalizationState,
    } = this.state;
    const resolvedPersonalizationState = this.resolvePersonalizationState();
    const showPersonalizationModal =
      isPersonalizationModalOpen ||
      ["asking", "generating"].includes(resolvedPersonalizationState);

    return (
      <>
        {showPersonalizationModal && (
          <PersonalizationModal
            enrolment={enrolment}
            onPersonalizationStateChange={
              this.handlePersonalizationModalStateChange
            }
            showModal={
              isPersonalizationModalOpen ||
              resolvedPersonalizationState == "asking"
            }
          />
        )}
        <RepersonalizationModal
          isModalOpen={isRepersonalizationModalOpen}
          closeModal={this.closeRepersonalizationModal}
          enrolment={enrolment}
          onStateChange={this.handleChangePersonalization}
          personalizationState={
            personalizationState || enrolment.personalization_state
          }
        />
      </>
    );
  };

  renderContent = () => {
    const {
      activeTab,
      answers,
      chapters,
      enrolment,
      loadingChapters,
      loadingResources,
      personalizationState,
      resources,
      showReviewModal,
    } = this.state;
    const { currentUser } = this.props;
    const courseState = getCourseOverviewState(enrolment);

    return (
      <>
        <StartCard
          answers={answers}
          chapters={chapters}
          currentUser={currentUser}
          enrolment={enrolment}
          onOpenPersonalizationModal={this.openPersonalizationModal}
          onOpenRepersonalizationModal={this.openRepersonalizationModal}
          personalizationState={
            personalizationState || enrolment.personalization_state
          }
          state={courseState}
        />
        <Tabs
          activeTab={activeTab}
          livesCount={enrolment?.lives_count}
          onChange={this.setActiveTab}
        />
        <div className="container">
          <ChaptersPanel
            activeTab={activeTab}
            answers={answers}
            chapters={chapters}
            currentUser={currentUser}
            enrolment={enrolment}
            loading={loadingChapters}
          />
          <ResourcesPanel
            activeTab={activeTab}
            loading={loadingResources}
            resources={resources}
          />
          <LivesPanel
            activeTab={activeTab}
            answers={answers}
            enrolment={enrolment}
            state={courseState}
          />
        </div>
        <NewReviewModal
          enrolmentId={enrolment.id}
          courseName={enrolment.course.name}
          showModal={showReviewModal}
          handleHideNewReviewModal={this.handleHideNewReviewModal}
          refreshCertificate={() => {}}
        />
        {this.renderPersonalizationModals()}
      </>
    );
  };

  render() {
    const { enrolment, loading } = this.state;
    const { currentUser } = this.props;
    const courseState = getCourseOverviewState(enrolment);

    return (
      <>
        <Icons />
        <main
          id="main"
          className={`enrolment-overview state-${courseState}${
            enrolment?.locked ? " is-course-locked" : ""
          }`}
        >
          <Masthead currentUser={currentUser} enrolment={enrolment} />
          {loading ? this.renderLoading() : this.renderContent()}
        </main>
      </>
    );
  }
}

export default CourseOverview;
